import {
  Combobox,
  ComboboxInput,
  ComboboxOption,
  ComboboxOptions,
} from "@headlessui/react";
import { MagnifyingGlassIcon } from "@heroicons/react/24/outline";
import { captureEvent } from "@Light/app/analytics";
import { Input } from "@Light/components/system/Input";
import { Spinner } from "@Light/components/system/Spinner";
import { useScaffold } from "@Light/scaffold";
import type { AddressSearchResponse } from "@Light/services/lightTypes";
import { formatAddressLine1, formatAddressLine2 } from "@Light/utils/address";
import { clsx } from "clsx";
import { twMerge } from "tailwind-merge";
import { MIN_QUERY_LENGTH, UseAddressSearch } from "./useAddressSearch";

const NOT_FOUND_ID = "__not_found__";

export type AddressSearchFieldProps = UseAddressSearch & {
  /** Called when the user picks an address from the floating list. */
  onSelect: (result: AddressSearchResponse) => void;
  /** Called when the user picks "I don't see my address". */
  onNotFound: () => void;
  /** Placeholder shown in the empty search input. */
  placeholder?: string;
  /** Class name to apply to the floating options container. */
  optionsClassName?: string;
  /** Render the floating list open regardless of focus. */
  forceOpen?: boolean;
  onFocus?: () => void;
  onBlur?: () => void;
};

/** Component powered by `useAddressSearch` */
export const AddressSearchField: React.FC<AddressSearchFieldProps> = ({
  query,
  onInputChange,
  results,
  isLoading,
  isError,
  isEmpty,
  multipleSameAddress,
  onSelect,
  onNotFound,
  placeholder = "Search address",
  forceOpen = false,
  optionsClassName,
  onFocus,
  onBlur,
}) => {
  const scaffold = useScaffold();

  // Only show the floating list once a search is underway, so an empty box
  // never flashes open while the field is being focused or lightly typed in.
  const showList = forceOpen || query.length >= MIN_QUERY_LENGTH;

  // Render loader only if we have no existing results.
  const showSpinner =
    results.length === 0 && (isLoading || (!isEmpty && !isError));

  const onChange = (key: string | null) => {
    if (!key) return;
    if (key === NOT_FOUND_ID) {
      captureEvent("enroll_address_not_found_clicked", { query });
      onNotFound();
      return;
    }
    const result = results[Number(key)];
    if (result) {
      onSelect(result);
    }
  };

  return (
    <Combobox<string | null> value={null} onChange={onChange} immediate>
      <div className="relative w-full">
        <Input
          inputComponent={ComboboxInput}
          icon={MagnifyingGlassIcon}
          isError={isError}
          value={query}
          onChange={(e) => {
            onInputChange(e.target.value);
          }}
          onFocus={onFocus}
          onBlur={onBlur}
          placeholder={placeholder}
          data-record="true"
          data-testid="address-search-input"
        />
        {showList && (
          <ComboboxOptions
            static={forceOpen}
            className={twMerge(
              "absolute left-0 right-0 top-full z-50 mt-1.5 max-h-72 overflow-auto rounded-lg bg-white shadow-lg pt-1 border focus:outline-none",
              optionsClassName,
            )}
          >
            {showSpinner ? (
              <div className="flex justify-center py-10">
                <Spinner className="h-6 w-6" />
              </div>
            ) : (
              <>
                {(isError || isEmpty) && (
                  <div
                    className={clsx(
                      "px-3 py-3 text-center text-sm",
                      isError
                        ? scaffold.page.colors.text.error
                        : scaffold.page.colors.text.muted,
                    )}
                  >
                    {isError
                      ? "Something went wrong. Please try again."
                      : "No matching addresses found."}
                  </div>
                )}
                {results.map((result, i) => (
                  <ComboboxOption
                    key={i}
                    value={String(i)}
                    data-testid="address-result"
                    className={clsx(
                      "pick-address-list-item flex cursor-pointer flex-col gap-0.5 px-3 py-2.5 outline-none last:border-b-0 data-focus:bg-slate-100",
                      i !== results.length - 1 && "border-b",
                    )}
                  >
                    <span
                      className={clsx(
                        "text-sm font-medium",
                        scaffold.page.colors.text.primary,
                      )}
                    >
                      {result.alias || formatAddressLine1(result)}
                    </span>
                    <span
                      className={clsx(
                        "truncate text-sm",
                        scaffold.page.colors.text.secondary,
                      )}
                    >
                      {formatAddressLine2(result)}
                    </span>
                    {multipleSameAddress && (
                      <span
                        className={clsx(
                          "text-sm",
                          scaffold.page.colors.text.muted,
                        )}
                      >
                        {result.esi_id} (ESI ID)
                      </span>
                    )}
                  </ComboboxOption>
                ))}
                <ComboboxOption
                  value={NOT_FOUND_ID}
                  data-testid="address-not-found"
                  className={clsx(
                    "flex cursor-pointer justify-center px-3 py-5 border-t text-center outline-none text-sm font-medium underline underline-offset-4 data-focus:bg-slate-100",
                    scaffold.page.colors.text.primary,
                  )}
                >
                  I don&apos;t see my address
                </ComboboxOption>
              </>
            )}
          </ComboboxOptions>
        )}
      </div>
    </Combobox>
  );
};
